<template>
  <div v-if="isVisible" :class="trackerClasses">
    <div class="progress-line">
      <div v-if="isInProgress" class="spinner" aria-hidden="true"></div>
      <div v-else-if="hasError" class="error-icon" aria-hidden="true">⚠</div>

      <span class="line-text" :class="{ 'line-text--error': hasError }">
        {{ lineText }}
      </span>

      <div v-if="showControls" class="inline-controls">
        <button
          @click="$emit('retry')"
          class="retry-button"
          :disabled="!isComplete && !hasError"
        >
          {{ $t('progressTracker.retry') }}
        </button>
        <button
          @click="$emit('cancel')"
          class="cancel-button"
          :disabled="isPolling"
        >
          {{ $t('progressTracker.cancel') }}
        </button>
      </div>

      <button
        v-if="showCloseButton"
        @click="$emit('close')"
        class="close-button"
        :aria-label="$t('progressTracker.closeAriaLabel')"
      >
        ×
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ProgressItem, JobRef, JobStatus } from '@lingvo-news/contracts';
import { localize } from '../../../utils/localize';

interface Props {
  progressItems: ProgressItem[];
  title?: string;
  showCloseButton?: boolean;
  showControls?: boolean;
  showLastUpdate?: boolean;
  variant?: 'default' | 'compact';
  isPolling?: boolean;
}

interface Emits {
  (e: 'progress-update', progress: ProgressItem[]): void;

  (e: 'complete', progress: ProgressItem[]): void;

  (e: 'error', error: string): void;

  (e: 'close'): void;

  (e: 'retry'): void;

  (e: 'cancel'): void;
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined, // Will use default from template or computed
  showCloseButton: false,
  showControls: false,
  showLastUpdate: false,
  variant: 'default',
  isPolling: false
});

const { t } = useI18n();

// Compute title if not provided
const displayTitle = computed(() => props.title || t('progressTracker.defaultTitle'));

const emit = defineEmits<Emits>();

// Check if any job has error
const hasError = computed(() => {
  if (!props.progressItems || props.progressItems.length===0) return false;
  return props.progressItems.some(item =>
      item.hasError ||
      item.status===JobStatus.Failed ||
      item.status===JobStatus.MaxRetriesExceeded
  );
});

// Get error message from failed items
const errorMessage = computed(() => {
  if (!props.progressItems || props.progressItems.length===0) return '';

  const failedItem = props.progressItems.find(item =>
      item.hasError ||
      item.status===JobStatus.Failed ||
      item.status===JobStatus.MaxRetriesExceeded
  );

  if (failedItem?.lastError?.userMessage) {
    return localize(failedItem.lastError.userMessage);
  }

  if (failedItem?.status===JobStatus.MaxRetriesExceeded) {
    return t('progressTracker.maxRetriesExceeded');
  }

  return t('progressTracker.jobFailed');
});

// Get retry info
const retryInfo = computed(() => {
  if (!props.progressItems || props.progressItems.length===0) return '';

  const failedItem = props.progressItems.find(item => item.hasError && item.retryCount);
  if (failedItem?.retryCount) {
    return t('progressTracker.retryCount', { count: failedItem.retryCount });
  }
  return '';
});

// Computed progress from props
const aggregatedProgress = computed(() => {
  if (!props.progressItems || props.progressItems.length===0) {
    return {
      total: 0,
      completed: 0,
      percentage: 0,
      status: t('progressTracker.initializing'),
      lastUpdate: Date.now()
    };
  }

  const total = props.progressItems.reduce((sum, item) => sum + item.total, 0);
  const completed = props.progressItems.reduce((sum, item) => sum + item.completed, 0);
  const percentage = total > 0 ? Math.round((completed / total) * 100):0;

  // Determine overall status
  const statuses = props.progressItems.map(i => i.status);
  let status = t('progressTracker.processing');

  if (statuses.every(s => s===JobStatus.Completed)) {
    status = t('progressTracker.completed');
  } else if (statuses.some(s => s===JobStatus.MaxRetriesExceeded)) {
    status = t('progressTracker.maxRetriesExceeded');
  } else if (statuses.some(s => s===JobStatus.Failed)) {
    status = t('progressTracker.failed');
  }
  const lastUpdate = Math.max(...props.progressItems.map(i => i.lastUpdate || 0));
  let currentOperation = '';
  for (let s of props.progressItems) {
    if (s.currentOperation && s.status===JobStatus.Running) {
      currentOperation = localize(s.currentOperation);
    }
  }
  return {
    total,
    completed,
    percentage,
    status,
    lastUpdate,
    currentOperation
  };
});

function toCompactProgressText(completed: number, total: number): string {
  if (total <= 0) return '';
  return `${completed}/${total}`;
}

const lineText = computed((): string => {
  const title = displayTitle.value;
  const operation = aggregatedProgress.value.currentOperation || aggregatedProgress.value.status;
  const progress = toCompactProgressText(aggregatedProgress.value.completed, aggregatedProgress.value.total);

  const titlePart = title ? title.trim(): '';
  const operationPart = operation ? operation.trim(): '';
  const progressPart = progress ? progress.trim(): '';

  const left = titlePart && operationPart ? `${titlePart} - ${operationPart}`: (titlePart || operationPart);
  return progressPart ? `${left} ${progressPart}`: left;
});

// Computed properties
const isComplete = computed(() => aggregatedProgress.value.percentage >= 100);

// Always visible since progress is managed externally
const isVisible = ref(true);

const isInProgress = computed(() => {
  return aggregatedProgress.value.status===t('progressTracker.processing') &&
      aggregatedProgress.value.percentage < 100;
});

const trackerClasses = computed(() => {
  return [
    'progress-tracker',
    props.variant==='compact' ? 'progress-tracker--compact':null,
    hasError.value ? 'progress-tracker--error':null
  ];
});

// Methods
function formatLastUpdate(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);

  if (diffSeconds < 60) {
    return t('progressTracker.secondsAgo', { count: diffSeconds });
  } else if (diffSeconds < 3600) {
    return t('progressTracker.minutesAgo', { count: Math.floor(diffSeconds / 60) });
  } else {
    return date.toLocaleTimeString();
  }
}
</script>

<style scoped>
.progress-tracker {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 999px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
  overflow: hidden;
  max-width: 100%;
}

.progress-tracker--error {
  border-color: #f5c6cb;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: #e9ecef;
  color: #495057;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(0, 0, 0, 0.10);
  border-top: 2px solid #4ade80;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-icon {
  font-size: 14px;
  flex-shrink: 0;
  color: #dc3545;
}

.progress-line {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  max-width: 100%;
}

.line-text {
  font-size: 13px;
  color: #495057;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70vw;
}

.line-text--error {
  color: #dc3545;
}

.inline-controls {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.retry-button,
.cancel-button {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.retry-button {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.retry-button:hover:not(:disabled) {
  background: #0056b3;
  border-color: #0056b3;
}

.cancel-button {
  background: #6c757d;
  color: white;
  border-color: #6c757d;
}

.cancel-button:hover:not(:disabled) {
  background: #545b62;
  border-color: #545b62;
}

.retry-button:disabled,
.cancel-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.progress-tracker--compact {
  border: 1px solid rgba(144, 238, 144, 0.4);
  box-shadow: 0 2px 8px rgba(144, 238, 144, 0.2);
  background: rgba(240, 255, 240, 0.95);
  color: #2d5a2d;
}

.progress-tracker--compact.progress-tracker--error {
  border-color: rgba(220, 53, 69, 0.5);
  background: rgba(255, 245, 245, 0.95);
}

.progress-tracker--compact .line-text {
  font-size: 12px;
  color: #2d5a2d;
}

.progress-tracker--compact .line-text--error {
  color: #dc3545;
}
</style>
